import { useState, useEffect, useRef } from 'react'
import { Volume2, VolumeX, RefreshCw, Play, Users } from 'lucide-react'
import Hls from 'hls.js'
import api from '../services/api'
import LoadingShimmer from './LoadingShimmer'
import useStreamManager from '../hooks/useStreamManager'

const SimpleCameraStream = ({ camera, onStreamReady, onStreamError, hideControls = false }) => {
    const [isMuted, setIsMuted] = useState(false)
    const [error, setError] = useState(null)
    const [streamUrl, setStreamUrl] = useState(null)
    const [isLoading, setIsLoading] = useState(false)
    const [isStreamStarted, setIsStreamStarted] = useState(false)
    const [viewerCount, setViewerCount] = useState(0)
    const videoRef = useRef(null)
    const hlsRef = useRef(null)
    
    // Stream manager for sharing integration
    const streamManager = useStreamManager()

    useEffect(() => {
        if (camera && !isStreamStarted) {
            const timer = setTimeout(() => {
                startStream();
            }, 100);

            return () => clearTimeout(timer);
        }
    }, [camera]);

    useEffect(() => {
        if (streamUrl && videoRef.current) {
            const timer = setTimeout(() => {
                initializeVideoPlayer();
            }, 200);

            return () => clearTimeout(timer);
        }
    }, [streamUrl]);

    useEffect(() => {
        return () => {
            if (hlsRef.current) {
                hlsRef.current.destroy()
            }
        }
    }, [])

    useEffect(() => {
        if (camera && !camera.isOnline && isStreamStarted) {
            console.log('Camera went offline, stopping stream attempts:', camera.name)
            setError('Camera is currently offline')
            setStreamUrl(null)
            setIsStreamStarted(false)
            if (hlsRef.current) {
                hlsRef.current.destroy()
                hlsRef.current = null
            }
        }
    }, [camera.isOnline, camera, isStreamStarted])

    // Auto-retry logic
    useEffect(() => {
        if (error && camera && camera.isOnline) {
            console.log(`Stream error for ${camera.name}, retrying in 5 seconds...`);
            const retryTimer = setTimeout(() => {
                // Keep shimmer active during auto-retry
                setIsLoading(true);
                handleRetry();
            }, 5000);

            return () => clearTimeout(retryTimer);
        }
    }, [error, camera]);

    const initializeVideoPlayer = () => {
        if (!videoRef.current || !streamUrl) return

        const absUrl = getAbsoluteStreamUrl(streamUrl)
        console.log('Initializing video player with URL:', absUrl)

        if (hlsRef.current) {
            hlsRef.current.destroy()
        }

        if (Hls.isSupported()) {
            const hls = new Hls({
                debug: false, // Disable debug for production
                enableWorker: true,
                lowLatencyMode: false, // Disable for maximum stability
                // Buffer settings - optimized for stability (no fluctuations)
                maxBufferSize: 200 * 1000 * 1000, // 200MB buffer for high quality
                maxBufferLength: 40, // 40 seconds buffer for maximum stability
                maxMaxBufferLength: 80, // 80 seconds max buffer
                maxBufferHole: 0.05, // Smaller buffer hole tolerance
                // Live settings - optimized for stability
                liveBackBufferLength: 20, // Keep 20 seconds of buffer for stability
                liveSyncDurationCount: 8, // Higher sync duration for stability
                liveMaxLatencyDurationCount: 20, // Higher latency for stability
                liveDurationInfinity: false, // Disable infinity duration for live
                // DISABLE adaptive bitrate completely to prevent fluctuations
                abrEwmaDefaultEstimate: 4000000, // 4Mbps estimate for highest quality
                abrBandWidthFactor: 1.0, // No bandwidth reduction
                abrBandWidthUpFactor: 1.0, // No bandwidth scaling
                abrMaxWithRealBitrate: false, // Disable real bitrate adaptation
                // Force highest quality level always
                startLevel: 0, // Force highest quality level
                capLevelToPlayerSize: false, // Don't cap level to player size
                capLevelOnFPSDrop: false, // Don't cap level on FPS drop
                // Quality stabilization - PREVENT any switching
                abrEwmaSlowVoD: 0.99, // Extremely slow adaptation
                abrEwmaSlowLive: 0.99, // Extremely slow adaptation
                abrEwmaFastVoD: 0.99, // Extremely slow adaptation
                abrEwmaFastLive: 0.99, // Extremely slow adaptation
                // Error handling - maximum tolerance
                highBufferWatchdogPeriod: 8, // Longer buffer watchdog period
                maxStarvationDelay: 15, // Longer starvation delay
                maxLoadingDelay: 15, // Longer loading delay
                nudgeOffset: 0.05, // Smaller nudge for stability
                nudgeMaxRetry: 15, // More retry attempts
                maxFragLookUpTolerance: 0.2, // Better tolerance for stability
                // Fragment settings - maximum retries
                fragLoadingMaxRetry: 15, // More fragment retries
                manifestLoadingMaxRetry: 15, // More manifest retries
                levelLoadingMaxRetry: 15, // More level retries
                // Timeout settings - extended for stability
                fragLoadingTimeOut: 40000, // 40 seconds timeout
                manifestLoadingTimeOut: 40000, // 40 seconds timeout
                levelLoadingTimeOut: 40000, // 40 seconds timeout
                // Disable features that can cause fluctuations
                enableSoftwareAES: true, // Enable software AES for better compatibility
                enableDateRangeMetadataCues: false, // Disable date range metadata
                enableEmsgMetadataCues: false, // Disable emsg metadata
                enableID3MetadataCues: false, // Disable ID3 metadata
                enableWebVTT: false, // Disable WebVTT
                enableIMSC1: false, // Disable IMSC1
                enableCEA708Captions: false, // Disable CEA708 captions
                // Force stable operation
                testBandwidth: false, // Disable bandwidth testing
                progressive: false // Disable progressive loading
            })

            hlsRef.current = hls

            hls.loadSource(absUrl)
            hls.attachMedia(videoRef.current)

            hls.on(Hls.Events.MANIFEST_PARSED, () => {
                console.log('Stream manifest loaded successfully for camera:', camera.name)
                console.log('Available quality levels for camera:', camera.name, hls.levels.length)
                
                // Allow initial load, then lock quality after video starts
                setTimeout(() => {
                    videoRef.current.play().catch((error) => {
                        console.log('Autoplay failed, but video will still load:', error)
                    })
                }, 500)
            })

            // Smart quality lock: Allow initial load, then lock after video starts
            let qualityLocked = false
            let initialFramesLoaded = false
            
            // Wait for video to start playing, then lock quality
            const lockQualityAfterLoad = () => {
                if (!qualityLocked && hls && !hls.destroyed && videoRef.current && !videoRef.current.paused) {
                    console.log('Video is playing - NOW locking quality for camera:', camera.name)
                    hls.currentLevel = 0
                    hls.loadLevel = 0
                    hls.autoLevelEnabled = false
                    qualityLocked = true
                    
                    if (hls.levelController) {
                        hls.levelController.manualLevel = 0
                    }
                }
            }

            // Listen for video events to know when to lock quality
            if (videoRef.current) {
                videoRef.current.addEventListener('loadeddata', () => {
                    console.log('Video data loaded for camera:', camera.name)
                    setTimeout(lockQualityAfterLoad, 1000) // Lock after 1 second of playback
                })
                
                videoRef.current.addEventListener('playing', () => {
                    console.log('Video started playing for camera:', camera.name)
                    // Turn off loading state only when video actually starts playing
                    setIsLoading(false)
                    
                    // Update viewer count
                    const currentViewerCount = streamManager.getViewerCount(camera._id);
                    setViewerCount(currentViewerCount);
                    
                    // Notify parent that stream is ready
                    if (onStreamReady) {
                        onStreamReady(camera._id);
                    }
                    if (!initialFramesLoaded) {
                        initialFramesLoaded = true
                        setTimeout(lockQualityAfterLoad, 2000) // Lock after 2 seconds of stable playback
                    }
                })
            }

            // Smart level switching prevention - only after quality is locked
            hls.on(Hls.Events.LEVEL_SWITCHING, (event, data) => {
                if (qualityLocked) {
                    console.log('BLOCKING quality switch for camera:', camera.name, 'attempted level:', data.level)
                    event.preventDefault && event.preventDefault()
                    hls.currentLevel = 0
                    hls.loadLevel = 0
                    hls.autoLevelEnabled = false
                } else {
                    console.log('Allowing initial quality selection for camera:', camera.name, 'level:', data.level)
                }
            })

            hls.on(Hls.Events.LEVEL_SWITCHED, (event, data) => {
                console.log('Quality level switched for camera:', camera.name, 'to level:', data.level)
                if (qualityLocked && data.level !== 0) {
                    console.log('FORCING back to highest quality level for camera:', camera.name)
                    setTimeout(() => {
                        hls.currentLevel = 0
                        hls.loadLevel = 0
                        hls.autoLevelEnabled = false
                    }, 100)
                }
            })

            // Monitor quality after it's locked
            const qualityEnforcer = setInterval(() => {
                if (hls && !hls.destroyed && qualityLocked) {
                    if (hls.currentLevel !== 0 || hls.loadLevel !== 0 || hls.autoLevelEnabled) {
                        console.log('Quality drift detected for camera:', camera.name, '- enforcing highest quality')
                        hls.currentLevel = 0
                        hls.loadLevel = 0
                        hls.autoLevelEnabled = false
                    }
                } else if (hls && hls.destroyed) {
                    clearInterval(qualityEnforcer)
                }
            }, 2000) // Check every 2 seconds after quality is locked

            // Block any attempt to change quality from bandwidth estimation (only after locked)
            hls.on(Hls.Events.LEVEL_UPDATED, (event, data) => {
                if (qualityLocked) {
                    console.log('Level updated event for camera:', camera.name, '- maintaining highest quality')
                    hls.currentLevel = 0
                    hls.autoLevelEnabled = false
                }
            })

            // Add buffer monitoring to prevent grey screens
            hls.on(Hls.Events.BUFFER_STALLED, () => {
                console.log('Buffer stalled for camera:', camera.name, '- attempting recovery')
                if (qualityLocked) {
                    hls.currentLevel = 0
                    hls.autoLevelEnabled = false
                }
            })

            hls.on(Hls.Events.BUFFER_APPENDING, () => {
                console.log('Buffer appending for camera:', camera.name)
                if (qualityLocked) {
                    hls.currentLevel = 0
                    hls.autoLevelEnabled = false
                }
            })

            hls.on(Hls.Events.BUFFER_APPENDED, () => {
                console.log('Buffer appended for camera:', camera.name)
                if (qualityLocked) {
                    hls.currentLevel = 0
                    hls.autoLevelEnabled = false
                }
            })

            hls.on(Hls.Events.BUFFER_FLUSHED, () => {
                console.log('Buffer flushed for camera:', camera.name)
                if (qualityLocked) {
                    hls.currentLevel = 0
                    hls.autoLevelEnabled = false
                }
            })

            hls.on(Hls.Events.ERROR, (event, data) => {
                console.error('HLS Error for camera:', camera.name, data)

                if (data.type === 'networkError' && data.details === 'manifestLoadError') {
                    console.log('Manifest not found (404) - stream may not be ready yet')
                    setError('Camera is starting up...')
                } else if (data.type === 'mediaError' && (data.details === 'bufferStalledError' || data.details === 'bufferSeekOverHole')) {
                    console.log('Buffer error - trying to recover')
                    // Don't show error for recoverable buffer issues
                    return
                } else if (data.type === 'mediaError' && data.details === 'fragParsingError') {
                    console.log('Fragment parsing error - trying to recover')
                    // Don't show error for fragment parsing issues
                    return
                } else if (data.type === 'mediaError' && data.details === 'keyLoadError') {
                    console.log('Key load error - trying to recover')
                    // Don't show error for key loading issues
                    return
                } else if (data.type === 'mediaError' && data.details === 'fragLoadError') {
                    console.log('Fragment load error - trying to recover')
                    // Don't show error for fragment loading issues
                    return
                } else if (data.type === 'mediaError' && data.details === 'levelLoadError') {
                    console.log('Level load error - trying to recover')
                    // Don't show error for level loading issues
                    return
                } else if (data.fatal) {
                    console.error('Fatal HLS error - attempting recovery')
                    // Try to recover from fatal errors
                    setTimeout(() => {
                        if (hlsRef.current) {
                            hlsRef.current.destroy()
                            hlsRef.current = null
                        }
                        startStream()
                    }, 2000)
                    setError('Optimizing connection...')
                }
            })

            // Add recovery event handlers
            hls.on(Hls.Events.MEDIA_ATTACHED, () => {
                console.log('Media attached for camera:', camera.name)
            })

            hls.on(Hls.Events.MANIFEST_LOADED, () => {
                console.log('Manifest loaded for camera:', camera.name)
            })

            hls.on(Hls.Events.LEVEL_LOADED, () => {
                console.log('Level loaded for camera:', camera.name)
            })

            hls.on(Hls.Events.FRAG_LOADED, () => {
                console.log('Fragment loaded for camera:', camera.name)
            })

            hls.on(Hls.Events.FRAG_PARSED, () => {
                console.log('Fragment parsed for camera:', camera.name)
            })

            videoRef.current.onerror = (error) => {
                console.error('Video error occurred for camera:', camera.name, error)
                setError('Adjusting video quality...')
            }

            videoRef.current.onloadstart = () => {
                console.log('Video loading started for camera:', camera.name)
            }

            videoRef.current.oncanplay = () => {
                console.log('Video can play for camera:', camera.name)
            }
        } else if (videoRef.current.canPlayType('application/vnd.apple.mpegurl')) {
            videoRef.current.src = absUrl
            
            // Add event listener for Safari to turn off loading when video starts playing
            videoRef.current.addEventListener('playing', () => {
                console.log('Video started playing for camera (Safari):', camera.name)
                setIsLoading(false)
                // Notify parent that stream is ready
                if (onStreamReady) {
                    onStreamReady(camera._id);
                }
            })
            
            videoRef.current.play().catch((error) => {
                console.log('Autoplay failed for Safari, but video will still load:', error)
            })
        } else {
            setError('Updating video player...')
        }
    }

    const startStream = async () => {
        if (!camera) return

        setIsLoading(true)
        // Keep error state briefly to maintain shimmer, then clear after loading is set
        setTimeout(() => setError(null), 50)
        try {
            console.log('Starting stream for camera:', camera._id, camera.name)
            const res = await api.get(`/cameras/${camera._id}/stream`)
            console.log('Stream response for camera:', camera.name, res.data)

            if (res.data.data.streamUrl) {
                setStreamUrl(res.data.data.streamUrl)
                setIsStreamStarted(true)
                
                // Register stream with sharing integration
                streamManager.registerStream(camera._id, {
                    url: res.data.data.streamUrl,
                    type: 'live',
                    userId: 'current_user' // TODO: Get from auth context
                });
                
                console.log('Stream URL set successfully for camera:', camera.name)
            } else {
                throw new Error('No stream URL received')
            }
        } catch (error) {
            console.error('Stream start error for camera:', camera.name, error)
            // Don't show harsh error messages - instead provide gentle feedback
            setError('Connecting to camera...')
            setIsStreamStarted(false)
            setIsLoading(false)
            // Notify parent about the error
            if (onStreamError) {
                onStreamError(camera._id, error);
            }
        }
    }

    const handleMute = () => {
        if (videoRef.current) {
            videoRef.current.muted = !isMuted
            setIsMuted(!isMuted)
        }
    }

    const handleRetry = () => {
        console.log('Retrying stream for camera:', camera.name)
        // Keep loading state active to maintain continuous shimmer
        setIsLoading(true)
        setError('Reconnecting...')
        setStreamUrl(null)
        setIsStreamStarted(false)
        if (hlsRef.current) {
            hlsRef.current.destroy()
            hlsRef.current = null
        }
        startStream()
    }

    const getAbsoluteStreamUrl = (url) => {
        if (!url) return ''
        if (url.startsWith('http')) return url
        const baseUrl = import.meta.env.VITE_API_STREAM_URL || import.meta.env.VITE_API_URL?.replace('/api', '') || 'http://localhost:3667'
        return baseUrl + url
    }

    if (!camera) {
        return (
            <div className="bg-black rounded overflow-hidden h-full flex items-center justify-center">
                <div className="text-center text-white">
                    <div className="text-4xl mb-2">📹</div>
                    <p>No camera</p>
                </div>
            </div>
        )
    }

    return (
        <div className="relative bg-black h-full">

            {/* Mute Button - Hide in fullscreen if hideControls is true */}
            {!hideControls && (
                <button
                    onClick={handleMute}
                    className="absolute top-2 right-2 bg-black bg-opacity-50 text-white p-1 rounded z-20"
                    title={isMuted ? 'Unmute' : 'Mute'}
                >
                    {isMuted ? <VolumeX className="h-3 w-3" /> : <Volume2 className="h-3 w-3" />}
                </button>
            )}

            {/* Refresh Button - Hide in fullscreen if hideControls is true */}
            {!hideControls && (
                <button
                    onClick={handleRetry}
                    className="absolute top-2 right-8 bg-black bg-opacity-50 text-white p-1 rounded z-20 hover:bg-opacity-70 transition-colors"
                    title="Refresh Stream"
                >
                    <RefreshCw className="h-3 w-3" />
                </button>
            )}

            {/* Viewer Count */}
            {viewerCount > 1 && (
                <div className="absolute bottom-2 left-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded flex items-center space-x-1 z-20">
                    <Users className="h-3 w-3" />
                    <span>{viewerCount} viewers</span>
                </div>
            )}

            {/* Video Container */}
            <div className="w-full h-full bg-black flex items-center justify-center relative">
                <div className="relative w-full h-full">
                    <video
                        ref={videoRef}
                        className="w-full h-full bg-black"
                        autoPlay
                        muted={isMuted}
                        style={{ display: streamUrl ? 'block' : 'none' }}
                        onError={() => setError('Adjusting video quality...')}
                    />
                </div>
                {(isLoading || error || !streamUrl) && (
                    <div className="absolute inset-0 bg-black bg-opacity-90">
                        <LoadingShimmer />
                    </div>
                )}
            </div>
        </div>
    )
}

export default SimpleCameraStream 